import { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Text, TextField } from '@backstage/ui';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';

/** Matches `serviceSpecSchema.name` — the name Control will accept. */
const VALID = /^[a-z][a-z0-9-]*$/;

/**
 * The service name, pre-filled from the repository you picked.
 *
 * # Why this is not just `EntityNamePicker`
 *
 * By the time somebody reaches this step they have already told the portal
 * which repository they mean, and the answer to "what should the service be
 * called" is almost always "the same as that". Asking again, with an empty box,
 * is asking a question we already know the answer to — and it is a question
 * with a wrong answer available: `Suntory-Toluna-ETL` is a fine repository name
 * and an illegal service name, and Control rejects it at the very end of the
 * form rather than here.
 *
 * So it suggests, normalised to what Control accepts, and stays editable. The
 * suggestion is applied only while the field is untouched — typing over it and
 * then going back a step must not silently undo what you typed.
 */
export const ServiceNameField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    formData,
    formContext,
    schema: { title, description },
  } = props;

  const [touched, setTouched] = useState(Boolean(formData));

  const suggestion = (
    formContext as {
      formData?: { repo?: { suggestedName?: string; name?: string } };
    }
  )?.formData?.repo?.suggestedName;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!touched && suggestion && !formData) {
      onChangeRef.current(suggestion);
    }
    // Re-runs when the suggestion arrives, which is after the repository is
    // chosen a step earlier.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion, touched]);

  const value = formData ?? '';
  const invalid = value.length > 0 && !VALID.test(value);
  const differs = Boolean(suggestion && value && value !== suggestion);

  return (
    <Box mb="3">
      <TextField
        id="service-name"
        name="service-name"
        label={title ?? 'Service name'}
        description={description}
        value={value}
        isRequired
        onChange={v => {
          setTouched(true);
          onChange(v);
        }}
      />
      {invalid && (
        <Text as="p" variant="body-small" style={{ color: 'var(--ar-err)' }}>
          Lowercase letters, digits and hyphens, starting with a letter. This
          becomes a DNS name and a Cloud Run service, so Control will refuse
          anything else.
        </Text>
      )}
      {suggestion && !touched && value === suggestion && (
        <Text as="p" variant="body-small" color="secondary">
          Suggested from your repository. Change it if the service should be
          called something else.
        </Text>
      )}
      {differs && (
        <Flex gap="2" align="center" style={{ marginTop: 4 }}>
          <Text variant="body-small" color="secondary">
            Your repository suggests{' '}
            <span style={{ fontFamily: 'var(--ar-font-mono)' }}>
              {suggestion}
            </span>
            .
          </Text>
          <Button
            size="small"
            variant="tertiary"
            onPress={() => {
              setTouched(false);
              onChange(suggestion!);
            }}
          >
            Use it
          </Button>
        </Flex>
      )}
    </Box>
  );
};
